import React, { ReactNode, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

interface HoverTipProps {
  children: ReactNode
  content: ReactNode
  placement?: "top" | "right" | "bottom" | "left"
  className?: string
  tipClassName?: string
  delay?: number
  maxWidth?: string
  dark?: boolean
  onlyOnClick?: boolean
  active?: boolean
}

const PortalHoverTip: React.FC<HoverTipProps> = ({
  children,
  content,
  placement = "top",
  className = "",
  tipClassName = "",
  delay = 100,
  maxWidth = "15rem",
  dark = true,
  onlyOnClick = false,
  active = true,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const updatePosition = () => {
    if (!triggerRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft

    const offset = 8

    let top = 0
    let left = 0

    switch (placement) {
      case "top":
        top = triggerRect.top + scrollTop - offset
        left = triggerRect.left + scrollLeft + triggerRect.width / 2
        break
      case "right":
        top = triggerRect.top + scrollTop + triggerRect.height / 2
        left = triggerRect.right + scrollLeft + offset
        break
      case "bottom":
        top = triggerRect.bottom + scrollTop + offset
        left = triggerRect.left + scrollLeft + triggerRect.width / 2
        break
      case "left":
        top = triggerRect.top + scrollTop + triggerRect.height / 2
        left = triggerRect.left + scrollLeft - offset
        break
    }

    setPosition({ top, left })
  }

  useEffect(() => {
    if (isOpen) {
      updatePosition()

      window.addEventListener("scroll", updatePosition, true)
      window.addEventListener("resize", updatePosition)

      return () => {
        window.removeEventListener("scroll", updatePosition, true)
        window.removeEventListener("resize", updatePosition)
      }
    }
  }, [isOpen])

  const handleMouseEnter = () => {
    if (onlyOnClick) return

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      setIsOpen(true)
    }, delay)
  }

  const handleMouseLeave = () => {
    if (onlyOnClick) return

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, delay)
  }

  const handleClick = () => {
    if (onlyOnClick) {
      setIsOpen(!isOpen)
    }
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const getTooltipStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: "absolute",
      zIndex: 9999,
      maxWidth,
      pointerEvents: "none",
    }

    if (placement === "top") {
      return {
        ...baseStyles,
        bottom: window.innerHeight - position.top,
        left: position.left,
        transform: "translateX(-50%)",
      }
    } else if (placement === "right") {
      return {
        ...baseStyles,
        top: position.top,
        left: position.left,
        transform: "translateY(-50%)",
      }
    } else if (placement === "bottom") {
      return {
        ...baseStyles,
        top: position.top,
        left: position.left,
        transform: "translateX(-50%)",
      }
    } else {
      return {
        ...baseStyles,
        top: position.top,
        right: window.innerWidth - position.left,
        transform: "translateY(-50%)",
      }
    }
  }

  const getArrowStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: "absolute",
      width: "8px",
      height: "8px",
      background: dark ? "#1f2937" : "white",
      transform: "rotate(45deg)",
    }

    if (placement === "top") {
      return { ...baseStyles, bottom: "-4px", left: "50%", marginLeft: "-4px" }
    } else if (placement === "right") {
      return { ...baseStyles, left: "-4px", top: "50%", marginTop: "-4px" }
    } else if (placement === "bottom") {
      return { ...baseStyles, top: "-4px", left: "50%", marginLeft: "-4px" }
    } else {
      return { ...baseStyles, right: "-4px", top: "50%", marginTop: "-4px" }
    }
  }

  const tipClasses = `py-2 px-3 rounded-md shadow-md text-sm leading-6 ${dark ? "bg-gray-800 text-white" : "bg-white text-gray-800 border border-gray-200"
    } ${tipClassName}`

  const canUseDOM = typeof window !== "undefined" && !!document.body

  if (!active) {
    return children
  }


  return (
    <>
      <div
        ref={triggerRef}
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {children}
      </div>

      {isOpen &&
        content &&
        canUseDOM &&
        createPortal(
          <div
            ref={tooltipRef}
            style={getTooltipStyles()}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className={tipClasses}>
              {content}
              <div style={getArrowStyles()} />
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

export default PortalHoverTip
